import { useMemo } from 'react';

import { makePairKey } from '@/components/TransactionModule/LimitMode/useLimitOrderRate';
import {
  taggedLimitRateSelector,
  useLimitRateStore,
} from '@/components/TransactionModule/limitRateStore';
import {
  getPriceInputsForUsdPriceFetch,
  getTokenPrices,
} from '@/components/TransactionModule/utils';

import { useUSDPrices } from '@/hooks/useUSDPrices';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { isUsdStableAddress } from '@/utils/multichain';

type UseLimitPriceLineParams = {
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
  side: 'sell' | 'buy';
  enabled: boolean;
};

export const useLimitPriceLine = ({
  sellToken,
  buyToken,
  side,
  enabled,
}: UseLimitPriceLineParams): number | undefined => {
  const taggedRate = useLimitRateStore(taggedLimitRateSelector);
  const pairKey = useMemo(() => makePairKey(sellToken, buyToken), [buyToken, sellToken]);
  const isReady = !!pairKey && taggedRate.pairKey === pairKey && taggedRate.rate.sell !== '';
  // On a USD-pegged charted token the converted trigger lands near $1 regardless of the limit,
  // so it says nothing and stretches the y-axis of an otherwise flat series.
  const chartToken = side === 'buy' ? buyToken : sellToken;
  const shouldShowLine = enabled && !isUsdStableAddress(chartToken?.address, chartToken?.chainId);
  const priceInputs = useMemo(
    () =>
      shouldShowLine
        ? getPriceInputsForUsdPriceFetch(sellToken, buyToken, sellToken?.issuer, buyToken?.issuer)
        : undefined,
    [buyToken, sellToken, shouldShowLine],
  );
  const { data: usdPrices } = useUSDPrices(priceInputs);

  if (!shouldShowLine || !isReady || !usdPrices) return undefined;

  const { sellTokenPrice, buyTokenPrice } = getTokenPrices(usdPrices, sellToken, buyToken);
  const rateValue = Number(side === 'sell' ? taggedRate.rate.sell : taggedRate.rate.buy);
  const counterTokenUsd = side === 'sell' ? buyTokenPrice?.priceUsd : sellTokenPrice?.priceUsd;
  const value = rateValue * (counterTokenUsd ?? 0);

  return Number.isFinite(value) && value > 0 ? value : undefined;
};
